import { useReducer } from "react";
import type { SessionPayload } from "../types";
import { isLikelyOtaAlias } from "../lib/emailHeuristics";

export type Step =
  | "welcome"
  | "confirmContact"
  | "register"
  | "companions"
  | "consent"
  | "eta"
  | "submitting"
  | "success"
  | "arrival";

export interface WizardState {
  step: Step;
  /**
   * Cached server snapshot. Refreshed whenever the React Query hook revalidates;
   * any screen that mutates the server should call `dispatch({type:'patchSession', …})`
   * to keep the UI consistent until the next refetch.
   */
  payload: SessionPayload | null;
}

export type WizardAction =
  | { type: "hydrate"; payload: SessionPayload; token: string }
  | { type: "patchSession"; patch: Partial<SessionPayload["session"]> }
  | { type: "goto"; step: Step }
  | { type: "next" }
  | { type: "back" };

// Base sequence used for next/back. `confirmContact` is intentionally NOT in
// the linear sequence — it's a preliminary step routed to on load when the
// primary email looks OTA-forwarded, and once dismissed the guest continues
// on the normal welcome → idUpload path (see nextStep hop below).
/*
 * NO DOCUMENT PHOTO STEP.
 *
 * Photographing, scanning or photocopying an identity document is not
 * something a hotel may do — Roman, 2026-09-01. The guest TYPES the details
 * the police record needs; an image of the document is not one of them.
 */
const SEQUENCE: Step[] = [
  "welcome",
  "register",
  "companions",
  "consent",
  "eta",
  "submitting",
  "success",
  "arrival",
];

function nextStep(curr: Step): Step {
  // From the OTA-alias confirmation, hop directly onto the normal path at
  // welcome so the guest gets the standard intro card next.
  if (curr === "confirmContact") return "welcome";
  const i = SEQUENCE.indexOf(curr);
  return SEQUENCE[Math.min(i + 1, SEQUENCE.length - 1)] ?? "arrival";
}

function prevStep(curr: Step): Step {
  if (curr === "confirmContact") return "confirmContact"; // no-op; first step
  const i = SEQUENCE.indexOf(curr);
  return SEQUENCE[Math.max(i - 1, 0)] ?? "welcome";
}

/**
 * Key for the sessionStorage flag that suppresses the OTA-confirm step after
 * the guest has dismissed it once. Keyed by magic-link token so a different
 * reservation on the same browser gets its own decision.
 */
export function otaConfirmedKey(token: string): string {
  return `webcheckin:contact-confirmed:${token}`;
}

function initialStepFor(payload: SessionPayload | null, token: string | null): Step {
  if (!payload) return "welcome";
  if (payload.session.status === "submitted") return "arrival";
  const primary = payload.session.occupants.find((o) => o.role === "primary");
  const email = primary?.email ?? payload.session.emailAddress;
  if (email && isLikelyOtaAlias(email) && token && typeof window !== "undefined") {
    try {
      if (window.sessionStorage.getItem(otaConfirmedKey(token))) return "welcome";
    } catch { /* Safari private-mode / storage-disabled — fall through */ }
    return "confirmContact";
  }
  return "welcome";
}

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "hydrate": {
      const step = state.payload ? state.step : initialStepFor(action.payload, action.token);
      return { step, payload: action.payload };
    }
    case "patchSession":
      if (!state.payload) return state;
      return {
        ...state,
        payload: {
          ...state.payload,
          session: { ...state.payload.session, ...action.patch },
        },
      };
    case "goto":
      return { ...state, step: action.step };
    case "next":
      return { ...state, step: nextStep(state.step) };
    case "back":
      return { ...state, step: prevStep(state.step) };
    default:
      return state;
  }
}

export function useWizard() {
  const [state, dispatch] = useReducer(reducer, { step: "welcome", payload: null });
  return { state, dispatch };
}
